import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import './Drawer.css'

interface Props {
  open: boolean
  onClose: () => void
  title: string
  subtitle?: string
  size?: 'md' | 'lg'
  headerAction?: ReactNode
  footer?: ReactNode
  children: ReactNode
}

/**
 * Right-side drawer. Esc closes. Backdrop click closes. Body scrolls
 * independently. Footer is sticky to the bottom.
 *
 * Matches the mock's drawer:
 *   - 560px wide on desktop, full-screen on mobile
 *   - backdrop rgba warm-overlay
 *   - 240ms slide-in
 */
export function Drawer({ open, onClose, title, subtitle, size = 'md', headerAction, footer, children }: Props): React.JSX.Element | null {
  useEffect(() => {
    if (!open) return undefined
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  return createPortal(
    <div className="drawer-portal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className={clsx('drawer', `drawer-${size}`)}>
        <header className="drawer-header">
          <div>
            <h2 className="drawer-title">{title}</h2>
            {subtitle ? <p className="drawer-subtitle">{subtitle}</p> : null}
          </div>
          <div className="drawer-header-actions">
            {headerAction}
            <button
              type="button"
              className="drawer-close"
              onClick={onClose}
              aria-label="Close drawer"
            >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            </button>
          </div>
        </header>
        <div className="drawer-body">{children}</div>
        {footer ? <footer className="drawer-footer">{footer}</footer> : null}
      </aside>
    </div>,
    document.body,
  )
}
